// src/components/common/ConsentBanner.jsx
//
// The one place a visitor says yes or no to analytics. Shown until they
// answer; reopened from the footer's "Cookie settings" (openConsentSettings).
// Both buttons are the same size and weight: DPDP consent has to be a free
// choice, and a big green "Accept" beside a grey text link is not one.
import React, { useEffect, useState } from 'react';
import {
  applyConsent, getConsent, setConsent, OPEN_SETTINGS_EVENT,
} from '../../utils/consent';
import { pageview } from '../../utils/ga';

export default function ConsentBanner() {
  const [open, setOpen] = useState(() => getConsent() === null);
  const current = getConsent();

  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, reopen);
  }, []);

  if (!open) return null;

  const choose = (value) => {
    setConsent(value);
    setOpen(false);
    if (value === 'granted') {
      applyConsent(window.location.pathname);
      pageview(window.location.pathname + window.location.search);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Privacy choices"
      className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-2xl rounded-2xl border border-gray-200 bg-white p-4 shadow-xl sm:p-5">
        <p className="text-sm font-semibold text-gray-900">Your privacy choices</p>
        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
          MintLink always uses essential storage to keep you signed in and make checkout work.
          With your OK we also use analytics (Google Analytics, Microsoft Clarity), Trustpilot
          review invitations and, on a seller&apos;s checkout, that seller&apos;s Meta Pixel.{' '}
          <strong className="font-semibold text-gray-800">Under 18? Choose Essential only.</strong>{' '}
          You can change this any time from <em>Cookie settings</em> at the bottom of the page.{' '}
          <a href="/privacy-policy#section-11" className="text-[#32764A] underline underline-offset-2">
            Privacy Policy
          </a>
        </p>
        {current && (
          <p className="mt-2 text-xs text-gray-500">
            Currently: {current === 'granted' ? 'analytics allowed' : 'essential only'}.
          </p>
        )}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => choose('denied')}
            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
          >
            Essential only
          </button>
          <button
            type="button"
            onClick={() => choose('granted')}
            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
          >
            Allow analytics
          </button>
        </div>
      </div>
    </div>
  );
}
